import { ComponentType } from '@angular/cdk/overlay';
import { ScrollingModule } from '@angular/cdk/scrolling';
import {
  Component,
  computed,
  effect,
  inject,
  Input,
  Signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { WidgetChangeDetectionObject } from '@api-response-types/forecast-series.response';
import {
  WidgetPortfolioTotals,
  WidgetPortfolioTotalsByYear,
  WidgetPortfolioTotalsChartData,
} from '@api-types/dtos/widget-portfolio-totals.dto';
import { ForecastVersionStatus } from '@api-types/enums/forecast-version-status.enum';
import { getFactorColumn, TOTALS_NAME } from '@api-types/helper/helper';
import {
  FormFieldComponent,
  FormInputSelectComponent,
  H3Component,
  PmButtonComponent,
  PmTooltipDirective,
} from '@libs/ui';
import { NgxVirtualSelectFieldOptionModel } from '@components/base-elements/virtual-select-field';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { InsightsHttpClient } from 'demand-forecasting/services/http/insights-http-client';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import { selectAvailableFilters } from 'demand-forecasting/store/filter/filter.selectors';
import {
  latestCompletedForecastSeries,
  selectForecastSeriesPossibleYears,
} from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import { selectForecastVersionByQueryParam } from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import { selectTargetUnit } from 'demand-forecasting/store/router-state/router.selectors';
import {
  BaseChartOptions,
  HighchartsOptions,
} from 'demand-forecasting/types/forecast-chart/highcharts-options';
import PortfolioTotalsChartOptions from 'demand-forecasting/types/forecast-chart/widgets/portfolio-totals-chart-options';
import Highcharts, { SeriesBarOptions } from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { isArray, isEqual } from 'lodash-es';
import { LucideScan } from '@lucide/angular';
import { DefaultSelectionPortfolioTotalsWidget } from '../../types/types';
import {
  WidgetActionsDirective,
  WidgetBaseComponent,
  WidgetContentDirective,
  WidgetHeaderDirective,
  WidgetTitleDirective,
} from '../widget-base/widget-base.component';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'pm-portfolio-totals-widget',
  templateUrl: './portfolio-totals-widget.component.html',
  imports: [
    WidgetBaseComponent,
    WidgetTitleDirective,
    PmTooltipDirective,
    WidgetHeaderDirective,
    WidgetContentDirective,
    HighchartsChartComponent,
    WidgetActionsDirective,
    TranslateModule,
    ScrollingModule,
    H3Component,
    PmButtonComponent,
    FormFieldComponent,
    FormInputSelectComponent,
    FormsModule,
  ],
})
export class PortfolioTotalsWidgetComponent extends WidgetBaseComponent<PortfolioTotalsWidgetComponent> {
  @Input({
    required: false,
  })
  targetUnit: string | undefined;

  @Input({
    required: false,
  })
  filter?: string;

  @Input({
    required: false,
  })
  year?: string;
  private readonly store = inject(Store);
  private readonly insightsHttpClient: InsightsHttpClient =
    inject(InsightsHttpClient);
  objectKeys = Object.keys;

  protected readonly scanIcon = LucideScan;

  private readonly availableFilters = this.store.selectSignal(
    selectAvailableFilters,
  );

  private readonly configuration = this.store.selectSignal(
    ConfigurationsSelectors.selectConfigurationByUrlParam,
  );

  private readonly factorColumn = computed(() => {
    const configuration = this.configuration();
    if (!configuration) {
      return undefined;
    }

    return this.targetUnit
      ? getFactorColumn(configuration, this.targetUnit)
      : undefined;
  });

  dataSeries: WidgetPortfolioTotals = {};
  dataSeriesByYear: WidgetPortfolioTotalsByYear = {};
  currentGroupName = '';
  currentYear = 0;
  filterNames: string[] = [];

  chartOptions: HighchartsOptions = BaseChartOptions.getEmptyChartOptions();
  chart?: Highcharts.Chart;
  chartUpdated = false;

  factorColumn$ = this.store.select(
    ConfigurationsSelectors.selectFactorColumnByUrlParam,
  );
  possibleYears = this.store.selectSignal(selectForecastSeriesPossibleYears);

  private readonly forecastSeries = this.store.selectSignal(
    latestCompletedForecastSeries,
  );
  private readonly forecastVersion = this.store.selectSignal(
    selectForecastVersionByQueryParam,
  );

  protected selectedTargetUnitFromUrl =
    this.store.selectSignal(selectTargetUnit);

  previousPortfolioTotalsChangeDetectionObject: WidgetChangeDetectionObject;

  groupsSelectOptions: Signal<NgxVirtualSelectFieldOptionModel<string>[]> =
    computed(() => {
      const groups = Object.keys(this.dataSeries);
      return groups.map((group) => ({
        value: group,
        label: group,
        disabled: false,
      }));
    });

  yearsSelectOptions: Signal<NgxVirtualSelectFieldOptionModel<number>[]> =
    computed(() =>
      this.possibleYears().map((year) => ({
        value: year,
        label: String(year),
        disabled: false,
      })),
    );

  public data: DefaultSelectionPortfolioTotalsWidget = inject(MAT_DIALOG_DATA, {
    optional: true,
  });

  constructor() {
    super();
    if (this.data) {
      this.targetUnit = this.data.targetUnit;
      this.filter = this.data.filter;
      this.year = this.data.year;
    }
    effect(() => {
      const forecastSeries = this.forecastSeries();
      const forecastVersion = this.forecastVersion();

      if (
        !forecastSeries ||
        !forecastVersion ||
        forecastVersion.status !== ForecastVersionStatus.FINISHED
      ) {
        return;
      }

      const newSeriesChangeDetectionObject: WidgetChangeDetectionObject = {
        _resultSetStatus: forecastSeries._resultSetStatus,
        configurationId: forecastSeries.configurationId,
        resultSetId: forecastVersion.id, // TODO forecastSeries.resultSetId seems to never get updated -> Fix via https://pacemaker-ai.atlassian.net/browse/PM-1674
        yAxis: forecastSeries.yAxis,
      };

      if (
        JSON.stringify(newSeriesChangeDetectionObject) ===
        JSON.stringify(this.previousPortfolioTotalsChangeDetectionObject ?? {})
      ) {
        return;
      }

      // Update previous values
      this.previousPortfolioTotalsChangeDetectionObject =
        newSeriesChangeDetectionObject;

      this.currentYear = this.year
        ? +this.year
        : new Date(
            forecastSeries.originalDates[
              forecastSeries.initialForecastIndex
            ][0],
          ).getFullYear();

      this.fetchData();
    });
  }
  private fetchData() {
    const forecastVersion = this.forecastVersion();
    const forecastSeries = this.forecastSeries();
    if (!forecastSeries || !forecastVersion) {
      console.error('No forecast series or version found');
      return;
    }
    this.isLoading = true;
    this.isError = false;
    this.insightsHttpClient
      .getPortfolioTotals(
        forecastVersion.forecastId,
        this.factorColumn(),
        this.currentYear.toString(),
        undefined,
      )
      .subscribe(
        (res) => {
          this.isLoading = false;
          if (!forecastSeries) {
            console.error('No forecast series found');
            return;
          }
          this.dataSeriesByYear = res;
          this.filterNames = this.availableFilters().map(
            (filterObj) => filterObj.column,
          );

          this.currentGroupName = this.filter
            ? this.filter
            : this.filterNames[0] || TOTALS_NAME;
          this.setupChart();
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
          this.isError = true;
        },
      );
  }

  setupChart() {
    // Deep copy dataSeriesByYear to avoid changing the original object
    const dataSeriesByYearCopy = structuredClone(this.dataSeriesByYear);
    this.dataSeries = Object.keys(dataSeriesByYearCopy[this.currentYear])
      .filter((key) => this.filterNames.includes(key) || key === TOTALS_NAME)
      .reduce((obj: Record<string, WidgetPortfolioTotalsChartData>, key) => {
        obj[key] = dataSeriesByYearCopy[this.currentYear][key];
        return obj;
      }, {});

    if (!this.dataSeries?.[this.currentGroupName]) {
      console.error('Data for group not found:', this.currentGroupName);
      return;
    }
    const groupData = this.dataSeries[this.currentGroupName];
    // Sorting and limiting to 10 entries
    const forecastValues = groupData.series[0].data;
    const actualValues = groupData.series[1].data;

    let sortedEntries = forecastValues
      .map((forecast, index) => ({
        category: groupData.categories[index],
        forecast,
        actual: actualValues[index],
        sum: forecast + actualValues[index],
      }))
      .sort((a, b) => b.sum - a.sum);
    if (!this.isDialogInstance) {
      sortedEntries = sortedEntries.slice(0, 10);
    }

    groupData.categories = sortedEntries.map((item) => item.category);
    groupData.series[0].data = sortedEntries.map((item) => item.forecast);
    groupData.series[1].data = sortedEntries.map((item) => item.actual);

    this.chartOptions = PortfolioTotalsChartOptions.getChartOptions();

    this.chartOptions.series =
      groupData.series as unknown as Highcharts.SeriesOptionsType[];

    if (!this.chartOptions.plotOptions?.series) {
      console.error('plotOptions.series not found in chartOptions');
      return;
    }
    this.chartOptions.plotOptions.series.stacking = 'normal';

    this.setXAxisCategories(groupData);

    this.chartOptions.xAxis.title.text = this.currentGroupName;

    const configuration = this.configuration();
    if (configuration) {
      this.chartOptions.yAxis.title.text =
        this.targetUnit ??
        this.selectedTargetUnitFromUrl() ??
        configuration.startForecastConfig.target;
    }

    if (this.chart) {
      Promise.resolve().then(() => {
        // @TODO Check if this update call is needed
        this.chart?.update(this.chartOptions, true, true);
        this.chartUpdated = true;
      });
    }
  }

  setXAxisCategories(chartData: WidgetPortfolioTotalsChartData) {
    if (this.chartOptions.series && this.chartOptions.series.length > 0) {
      const barSeries = this.chartOptions.series[0] as SeriesBarOptions;
      if (barSeries.data) {
        if (
          'xAxis' in this.chartOptions &&
          !Array.isArray(this.chartOptions.xAxis) &&
          this.chartOptions.xAxis !== undefined
        ) {
          this.chartOptions.xAxis.categories = chartData.categories;
        }
      }
    }
  }

  setChartInstance(chart: Highcharts.Chart): void {
    this.chart = chart;
  }

  onGroupChange(value: string | string[]): void {
    // this is no multiselect therefore the string[] case cannot happen
    if (isArray(value)) {
      return;
    }
    this.currentGroupName = value;
    this.setupChart();
  }

  async onYearChange(value: number | number[]) {
    if (isArray(value) || isEqual(value, this.currentYear)) {
      return;
    }
    this.currentYear = value;
    this.fetchData();
  }

  // Override the showFullScreen method from the WidgetBaseComponent to handle afterClosed event properly
  showFullScreen(
    componentType: ComponentType<PortfolioTotalsWidgetComponent>,
    filterSelection?: DefaultSelectionPortfolioTotalsWidget,
  ): MatDialogRef<PortfolioTotalsWidgetComponent> | null {
    const dialog = super.showFullScreen(componentType, filterSelection);
    dialog?.afterClosed().subscribe(() => {
      this.currentYear = +dialog.componentInstance.currentYear.toString();
      this.filter = dialog.componentInstance.currentGroupName;
      this.fetchData();
    });
    return null;
  }

  get PortfolioTotalsWidgetComponent(): typeof PortfolioTotalsWidgetComponent {
    return PortfolioTotalsWidgetComponent;
  }
}
